import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, SimpleChange, ViewEncapsulation } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { I18NEXT_NAMESPACE, I18NextService } from 'angular-i18next';
import { getFormControlPath } from 'core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ValidationMessageModel } from 'shared/components/generic/validation-error/validation-message.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    exportAs: 'validationError',
    selector: 'validation-error',
    styleUrls: ['./validation-error.component.scss'],
    template: `
        <ng-template ngFor let-error [ngForOf]="validationMessages$ | async">
            <div class="s7-form-error s7-form-error-xs" [class.pl-0]="resetPadding" [class.mb-2]="indent" [class.mb-0]="!indent" [innerHTML]="errorMessage ? errorMessage : error.message"></div>
        </ng-template>
    `
})
export class ValidationErrorComponent implements OnChanges, OnDestroy {
    @Input()
    public for: AbstractControl | null = null;
    @Input()
    public errorMessage: string;
    @Input()
    public indent: boolean;
    @Input()
    public resetPadding: boolean;
    public validationMessages$: BehaviorSubject<ValidationMessageModel[]> = new BehaviorSubject([]);
    private statusSubscription: Subscription;
    private formControlPath: string;

    constructor(@Inject(I18NEXT_NAMESPACE) private i18nextNamespace: string, private i18next: I18NextService) {}

    public ngOnDestroy(): void {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
    }
    public ngOnChanges(changes: { for?: SimpleChange }): void {
        if (changes.for) {
            this.bindControlListener();
        }
    }
    public bindControlListener(): void {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
        if (this.for) {
            this.statusSubscription = this.for.statusChanges.pipe(startWith(this.for.status)).subscribe(() => {
                this.buildValidationMessages();
            });
        }
    }
    private buildValidationMessages(): void {
        // markForCheck используем вместо события, поскольку нам нужно, чтобы проверка прошла от самого root вплоть до текущего компонента.
        // Так же это дешевле, чем бросать event, посколько события за один цикл работы с формой могут выбрасываться хоть по 10 раз
        const result: ValidationMessageModel[] = [];
        if (this.for == null || this.for.status !== 'INVALID' || this.for.errors == null) {
            this.validationMessages$.next([]);
            return;
        }
        const errorsArray: any = this.for.errors;
        for (const key of Object.keys(this.for.errors)) {
            this.appendValidationMessagesFor(errorsArray, key, result);
        }
        this.validationMessages$.next(result);
    }

    private appendValidationMessagesFor(errorsArray: any, key: string, appendTo: ValidationMessageModel[]): void {
        const value = errorsArray[key];
        let params = null;
        if (value instanceof Object) {
            params = value;
        } else if (value !== true) {
            params = {};
            params[key] = value;
        }
        if (key === 'serverErrors') {
            // С сервера может прийти множество сообщений под одним ключом. Разделяем их
            (Array.isArray(params) ? params : [params]).forEach(message => {
                appendTo.push(new ValidationMessageModel(this.i18next, key, message, this.i18nextNamespace));
            });
        } else {
            this.formControlPath = (this.formControlPath || getFormControlPath(this.for)) ?? null as unknown as string;
            appendTo.push(new ValidationMessageModel(this.i18next, key, params, this.i18nextNamespace, this.formControlPath));
        }
    }
}
